import NextHead from '@quicktools/shared-components/components/common/NextHead';
import Button from '@quicktools/shared-components/components/core/Button';
import { SingleToolHeader } from '@quicktools/shared-components/components/common/SingleToolHeader';
import { WithBreadcrumb } from '@quicktools/shared-components/components/core/Breadcrumb';
import { getEmojiImgUrl } from '@quicktools/emojis';
import formatForAnalytics from '@quicktools/shared-components/utils/formatForAnalytics';
import Link from 'next/link';
import IconArrowRightLarge from '@picsart/icons/IconArrowRightLarge';
import cls from 'classnames';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

import analytics from '../../services/analytics';

import NameList from './NameList';
import OtherEmojis from './OtherEmojis';
import WithEmojiCopy from './WithEmojiCopy';
import useStyles from './styles';
import { IEmojiPageProps } from './types';
import Paragraphs from './Paragraphs';

const getImgs = (name: string) => {
  return [
    {
      text: 'Apple',
      url: getEmojiImgUrl(name, 'apple', '/'),
      platform: 'apple',
    },
    {
      text: 'Google Noto Color Emoji',
      url: getEmojiImgUrl(name, 'google', '/'),
      platform: 'google',
    },
    {
      text: 'Samsung',
      url: getEmojiImgUrl(name, 'samsung', '/'),
      platform: 'samsung',
    },
    {
      text: 'Microsoft',
      url: getEmojiImgUrl(name, 'microsoft', '/'),
      platform: 'microsoft',
    },
  ];
};

function EmojiPage({ id, glyph, name, categoryId, paragraphs, CategoryIcon }: IEmojiPageProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const breadcrumb = t(`emojiPage.${id}.breadcrumb`);
  const longName = t(`emojiPage.${id}.longName`);
  const categoryText = t(`emojiPage.${id}.categoryText`);
  const seoTitle = t(`emojiPage.${id}.seoTitle`);
  const seoDescription = t(`emojiPage.${id}.seoDescription`);

  const sendEvents = (actionType: string) => {
    analytics.pageClick({
      page_path: location.pathname,
      action_type: actionType,
    });
  };

  useEffect(() => {
    analytics.emojiPageOpen(name, 'single_emoji_pages');
  }, []);

  return (
    <>
      <NextHead
        title={seoTitle}
        description={seoDescription}
        canonicalPath={`text/emojis/${id}/`}
        imageUrl={getImgs(name)[0].url}
      />

      <div className={classes.root}>
        <SingleToolHeader
          analytics={analytics}
          toolName={formatForAnalytics(longName)}
          className="header"
        />
        <WithBreadcrumb />
        <div className={classes.container}>
          <main className={classes.main}>
            <div className={classes.layout}>
              <section>
                <h1 className={classes.title}>
                  {glyph} {longName}
                </h1>
                <h2 className={classes.subtitle}>{t('general.emojiMeaning')}</h2>
                <Paragraphs id={id} n={paragraphs} />
                <h3 className={classes.boldHeading}>{t('general.alsoCalled')}</h3>
                <NameList id={id} n={3} />
                <div className={cls(classes.inlineSection)}>
                  <h2 className={cls(classes.subtitle, classes.subtitleInline)}>
                    {t('general.copyPaste')}
                  </h2>
                  <div className={classes.copyContainer}>
                    <div className={classes.glyphWithBackground}>{glyph}</div>
                    <WithEmojiCopy glyph={glyph} sendAnalyticsEvent={() => sendEvents('copy')}>
                      <button className={classes.copyButton}>{t('general.copy')}</button>
                    </WithEmojiCopy>
                  </div>
                </div>
              </section>

              <aside>
                <Link href="/" passHref={true}>
                  <Button
                    variant="primary"
                    className={classes.navButton}
                    onClick={() => sendEvents('go_to_all_emojies')}
                  >
                    {t('general.goToAll')}
                    <IconArrowRightLarge className={classes.navButtonIcon} />
                  </Button>
                </Link>
                <h2 className={classes.subtitle}>{t('general.category')}</h2>
                <Link href={`/${categoryId}`} passHref>
                  <a>
                    <div
                      className={classes.categoryContainer}
                      onClick={() => sendEvents(`category_${categoryId}`)}
                    >
                      <CategoryIcon className={classes.categoryIcon} />
                      <span className={classes.categoryText}>{categoryText}</span>
                    </div>
                  </a>
                </Link>
                <h2 className={classes.subtitle}>{t('general.other')}</h2>
                <OtherEmojis exclude={id} sendEvent={sendEvents} />
              </aside>
            </div>

            <section>
              <h2 className={cls(classes.subtitle, classes.subtitlePadded)}>
                {t('general.variants')}
              </h2>
              <ul className={classes.imgContainer}>
                {getImgs(name).map(({ text, url, platform }) => (
                  <WithEmojiCopy
                    key={name + text}
                    glyph={glyph}
                    sendAnalyticsEvent={() => sendEvents(`platform_click_${platform}`)}
                  >
                    <li className={classes.imgEntry}>
                      <span className={classes.imgText}>{text}</span>
                      <img className={classes.img} src={url} />
                    </li>
                  </WithEmojiCopy>
                ))}
              </ul>
            </section>

            <section>
              <p className={classes.note}>{t('general.note')}</p>
            </section>
          </main>
        </div>
      </div>
    </>
  );
}

export default EmojiPage;
